import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import apiClient from '../config/api';

const STATUS = {
  idle: 'idle',
  verifying: 'verifying',
  success: 'success',
  error: 'error',
};

const parseQuery = search => {
  const params = new URLSearchParams(search);
  const email = params.get('email')?.trim();
  const token = params.get('token')?.trim();
  return { email, token };
};

const appendSpinnerStyles = () => {
  const styleId = 'confirm-account-spinner-style';
  if (document.getElementById(styleId)) {
    return;
  }
  const style = document.createElement('style');
  style.id = styleId;
  style.innerHTML = `@keyframes confirm-account-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`;
  document.head.appendChild(style);
};

const ConfirmAccount = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { email, token } = useMemo(() => parseQuery(location.search), [location.search]);
  const [status, setStatus] = useState(STATUS.idle);
  const [message, setMessage] = useState('');
  const [resendStatus, setResendStatus] = useState('idle');

  useEffect(() => {
    appendSpinnerStyles();
  }, []);

  useEffect(() => {
    let redirectTimer;

    const verify = async () => {
      if (!email || !token) {
        setStatus(STATUS.error);
        setMessage('Invalid confirmation link.');
        return;
      }

      setStatus(STATUS.verifying);
      setMessage('Verifying your email, please wait...');

      try {
        const response = await apiClient.post('/auth/confirm-account', { email, token });
        setStatus(STATUS.success);
        const respMsg = response?.data?.message || 'Your email has been verified successfully.';
        setMessage(respMsg);

        redirectTimer = setTimeout(() => navigate('/login', { replace: true }), 4000);
      } catch (err) {
        const apiError = err?.response?.data?.error;
        setStatus(STATUS.error);
        setMessage(apiError || 'We were unable to verify your email. Please request a new link.');
      }
    };

    verify();

    return () => {
      if (redirectTimer) {
        clearTimeout(redirectTimer);
      }
    };
  }, [email, token, navigate]);

  const handleResend = async () => {
    if (!email) {
      setResendStatus('error');
      setMessage('Email address missing; cannot resend confirmation.');
      return;
    }

    setResendStatus('pending');
    try {
      const response = await apiClient.post('/auth/resend-confirmation', { email });
      setResendStatus('success');
      setMessage(response?.data?.message || 'A new confirmation email has been sent.');
    } catch (err) {
      const apiError = err?.response?.data?.error;
      setResendStatus('error');
      setMessage(apiError || 'Failed to resend confirmation email.');
    }
  };

  const showResend = status === STATUS.error || resendStatus === 'success' || resendStatus === 'error';

  return (
    <div style={styles.wrapper}>
      <div style={styles.card}>
        <h1 style={styles.heading}>Email Confirmation</h1>
        {email ? <p style={styles.subheading}>Email: <strong>{email}</strong></p> : null}
        <p style={styles.message}>{message}</p>

        {status === STATUS.verifying && <div style={styles.spinner} aria-hidden="true" />}

        {status === STATUS.success && (
          <p style={styles.helperText}>
            You will be redirected to the login page shortly. If that does not happen,
            <Link to="/login" style={styles.link}> click here</Link>.
          </p>
        )}

        {showResend && (
          <div style={styles.resendWrapper}>
            <button
              style={{
                ...styles.resendButton,
                ...(resendStatus === 'pending' ? styles.resendButtonDisabled : {}),
              }}
              onClick={handleResend}
              disabled={resendStatus === 'pending'}
            >
              {resendStatus === 'pending' ? 'Sending...' : 'Resend Confirmation Email'}
            </button>
          </div>
        )}

        <div style={styles.footerLinks}>
          <Link to="/login" style={styles.link}>Back to Login</Link>
          <span style={styles.separator}>|</span>
          <Link to="/register" style={styles.link}>Create an account</Link>
        </div>
      </div>
    </div>
  );
};

const styles = {
  wrapper: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #0f162b, #1f3c88)',
    padding: '32px',
    boxSizing: 'border-box',
  },
  card: {
    width: '100%',
    maxWidth: '480px',
    backgroundColor: '#fff',
    borderRadius: '16px',
    boxShadow: '0 24px 48px rgba(0, 0, 0, 0.15)',
    padding: '32px',
    textAlign: 'center',
  },
  heading: {
    margin: '0 0 8px',
    fontSize: '28px',
    fontWeight: 700,
    color: '#1f3c88',
  },
  subheading: {
    margin: '0 0 16px',
    color: '#5f6c7b',
  },
  message: {
    fontSize: '16px',
    color: '#344054',
    marginBottom: '24px',
    lineHeight: 1.5,
  },
  helperText: {
    marginTop: '16px',
    color: '#5f6c7b',
  },
  link: {
    color: '#1f3c88',
    textDecoration: 'underline',
    fontWeight: 600,
  },
  resendWrapper: {
    marginBottom: '24px',
  },
  resendButton: {
    background: 'linear-gradient(135deg, #1f3c88, #39a0ed)',
    color: '#fff',
    border: 'none',
    borderRadius: '999px',
    padding: '12px 28px',
    fontSize: '16px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    boxShadow: '0 12px 20px rgba(31, 60, 136, 0.25)',
  },
  resendButtonDisabled: {
    opacity: 0.7,
    cursor: 'not-allowed',
    transform: 'none',
    boxShadow: 'none',
  },
  footerLinks: {
    display: 'flex',
    justifyContent: 'center',
    gap: '8px',
    color: '#5f6c7b',
    fontSize: '14px',
  },
  separator: {
    color: '#9BA3AF',
  },
  spinner: {
    width: '48px',
    height: '48px',
    border: '5px solid rgba(31, 60, 136, 0.15)',
    borderTopColor: '#1f3c88',
    borderRadius: '50%',
    margin: '0 auto 24px',
    animation: 'confirm-account-spin 1s linear infinite',
  },
};

export default ConfirmAccount;

